import { fetchAuthSession } from '@aws-amplify/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import config from '../../../../config';
import useAudioConfigContext from '../../AudioConfigContext/useAudioConfigContext';
import useSessionContextContext from '../SessionContextContextProvider/useSessionContextContext';
import useSessionDetectionContext from '../SessionDetectionContextProvider/useSessionDetectionContext';
import useSessionInfoContext from '../SessionInfoContextProvider/useSessionInfoContext';
import useSessionLanguageContext from '../SessionLanguageContextProvider/useSessionLanguageContext';
import useAudioCapture from './Hooks/useAudioCapture';
import useEnqueueTrigger from './Hooks/useEnqueueTrigger';
import useSpeechRecognition from './Hooks/useSpeechRecognition';
import arrayBufferToBase64 from './Hooks/utils/arrayBufferToBase64';
import { AudioWebhookResponse, SessionConnectionContext } from './useSessionConnectionContext';

export default function SessionConnectionContextProvider({ children }: { children: React.ReactNode }) {
  const flag = useFlag('send-raw-audio-bytes');
  const userQuery = useQuery(['cognito-user'], () => fetchAuthSession());

  const { session, setSessionId, endCause } = useSessionInfoContext();
  const { setLatestDetection } = useSessionDetectionContext();
  const { context } = useSessionContextContext();
  const [enabled, setEnabled] = React.useState<boolean>(false);
  const audioConfig = useAudioConfigContext();
  const [endingSession, setEndingSession] = React.useState<string | null>(null);
  const { sessionLanguage } = useSessionLanguageContext();

  const {
    sendMessage,
    sendJsonMessage, lastJsonMessage, readyState,
  } = useWebSocket<AudioWebhookResponse>(config.Live.AudioWebsocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => true,
    onError: () => console.error('Audio websocket connection error'),
    protocols: ['cogneed-ws-protocol', userQuery.data?.tokens?.idToken?.toString()],
    onOpen: () => {
      if (session) {
        sendJsonMessage({
          type: 'settings',
          asrLanguage: sessionLanguage,
          projectId: session.projectId,
          sessionId: session.id,
          date: Date.now() * 1e6,
        });
      }
    },
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
  }, enabled);

  const lastTranscript = useSpeechRecognition(sessionLanguage);
  const audioConfigRef = React.useRef(audioConfig);

  const [lastAudioBlob, restart] = useAudioCapture(!!session && readyState === ReadyState.OPEN, audioConfig);
  React.useEffect(() => {
    if (audioConfigRef.current.deviceId !== audioConfig.deviceId) {
      sendJsonMessage({
        type: 'clear-webm-audio',
        date: Date.now() * 1e6,
      });
      restart();
    }
    audioConfigRef.current = audioConfig;
  }, [audioConfig]);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    let isMounted = true;
    if (session) {
      setEnabled(true);
    } else {
      setTimeout(() => {
        if (isMounted) {
          setEnabled(false);
        }
      }, 200);
    }
    return () => {
      isMounted = false;
    };
  }, [session]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && session?.id) {
      sendJsonMessage({
        type: 'settings',
        asrLanguage: sessionLanguage,
        projectId: session.projectId,
        sessionId: session.id,
        date: Date.now() * 1e6,
      });
    }
  }, [session?.id, session?.projectId, sessionLanguage, readyState]);

  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);
  React.useEffect(() => {
    sessionIdRef.current = session?.id ?? null;
  }, [session?.id]);

  const enqueueTrigger = useEnqueueTrigger();

  React.useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type) {
      switch (lastJsonMessage.type) {
        case 'endSessionAck':
          if (sessionIdRef.current === lastJsonMessage.sessionId) {
            setSessionId(null);
            setEnabled(false);
            setEndingSession(null);
          }
          break;
        case 'sessionActivityTrigger':
          // eslint-disable-next-line no-case-declarations
          const subKey = lastJsonMessage.activate ? 'activate' : 'deactivate';
          console.info('affichage');
          enqueueTrigger(
            'activityChange',
            subKey,
            () => {
              sendJsonMessage({
                type: 'confirm-session-activity-change',
                date: Date.now() * 1e6,
                activate: lastJsonMessage.activate,
                detection: lastJsonMessage.detection,
              });
            },
          );
          break;

        case 'sessionTimeout':
          enqueueTrigger(
            'sessionTimeout',
            'body',
            () => {
              setEndingSession(sessionIdRef.current);
            },
          );
          break;

        case 'session-change':
          queryClient.setQueryData(
            ['getLiveSession', { input: { id: lastJsonMessage.sessionId } }],
            { getSession: { ...session, id: lastJsonMessage.sessionId, detections: [] } },
          );
          queryClient.invalidateQueries(['getLiveSession', { input: { id: lastJsonMessage.sessionId } }]);

          setSessionId(lastJsonMessage.sessionId);
          restart();
          if (lastJsonMessage.detection.detection) {
            setLatestDetection(lastJsonMessage.detection.detection);
          }
          break;

        case 'detection':
          setLatestDetection(lastJsonMessage.message.detection);
          break;

        case 'restart-audio-stream':
          restart();
          sendJsonMessage({
            type: 'clear-webm-audio',
            date: Date.now() * 1e6,
          });
          break;

        case 'callStart':
          break;

        default:
          console.error('Unknown message type', lastJsonMessage);
          break;
      }
    }
  }, [lastJsonMessage]);

  React.useEffect(() => {
    if (lastAudioBlob) {
      if (flag) {
        sendMessage(lastAudioBlob);
      } else {
        sendJsonMessage({
          type: 'audio',
          date: Date.now() * 1e6,
          webm: arrayBufferToBase64(lastAudioBlob),
        });
      }
    }
  }, [lastAudioBlob]);

  React.useEffect(() => {
    if (lastTranscript) {
      sendJsonMessage({
        ...lastTranscript,
        date: Date.now() * 1e6,
      });
    }
  }, [lastTranscript]);

  React.useEffect(() => {
    if (sessionIdRef.current) {
      sendJsonMessage({
        type: 'settings',
        context: context ?? '',
        date: Date.now() * 1e6,
      });
    }
  }, [context, sessionIdRef]);

  React.useEffect(() => {
    if (endCause) {
      sendJsonMessage({
        type: 'endSession',
        cause: endCause,
        date: Date.now() * 1e6,
      });
      if (endCause === 'ManualSessionTermination') {
        setEndingSession(sessionIdRef.current);
      }
    }
  }, [endCause, sessionIdRef]);

  React.useEffect(() => {
    if (endingSession) {
      const timeout = setTimeout(() => {
        if (endingSession === sessionIdRef.current) {
          setEnabled(false);
          setSessionId(null);
        }
      }, 1000);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [endingSession, sessionIdRef]);

  const value = React.useMemo(() => ({
    latestMessage: lastJsonMessage,
  }), [lastJsonMessage]);

  return (
    <SessionConnectionContext.Provider value={value}>
      {children}
    </SessionConnectionContext.Provider>
  );
}
